import { ApolloClient, InMemoryCache, from, fromPromise, HttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getConfig } from "../config";

const httpLink = new HttpLink({
  uri: getConfig(
    "REACT_APP_GRAPHQL_URI",
    "http://localhost:4000/graphql",
  ),
});

/**
 * Handle the per-error logic from an Apollo `onError` callback.
 *
 * Exported so it can be unit-tested independently of the full Apollo link
 * pipeline. Returns an Observable when the error is a recoverable
 * `InvalidToken` (MSAL token refresh + operation replay), otherwise returns
 * undefined after dispatching the appropriate `CustomEvent`.
 *
 * Order matters: `InvalidToken` is handled first and returns early so the
 * `Error:GraphQLError` branch is never reached for a recoverable token expiry.
 *
 * If `requestAccessToken` itself returns null — meaning MSAL could not
 * renew the token even with a popup — the user's session is unrecoverable
 * and we reload the page so MSAL can redirect to the login screen. This
 * prevents a silent "no data" state from the `.filter(Boolean)` swallowing
 * the null value.
 *
 * `reload` is accepted as an optional override so tests can verify the
 * reload path without actually reloading the test environment.
 */
export const handleGraphQLErrors = ({
  graphQLErrors,
  forward,
  operation,
  requestAccessToken,
  reload = () => window.location.reload(),
}) => {
  if (!graphQLErrors) return;
  for (const error of graphQLErrors) {
    const { message, extensions } = error || {};
    if (message === "Context creation failed: InvalidToken") {
      return fromPromise(requestAccessToken?.()).flatMap(freshToken => {
        if (!freshToken) {
          // MSAL could not renew — force a full re-login.
          reload();
          return fromPromise(Promise.resolve(null)).filter(() => false);
        }
        return forward(operation);
      });
    } else if (message === "Unauthorized") {
      window.dispatchEvent(
        new CustomEvent("Error:Unauthorized", {
          detail: { permissions: extensions?.permissions || [] },
        }),
      );
    } else {
      window.dispatchEvent(
        new CustomEvent("Error:GraphQLError", {
          detail: { message },
        }),
      );
    }
  }
};

const getErrorLink = props =>
  onError(({ forward, operation, graphQLErrors, networkError }) => {
    const { requestAccessToken } = props || {};
    const result = handleGraphQLErrors({
      graphQLErrors,
      forward,
      operation,
      requestAccessToken,
    });
    if (networkError) console.log(`[Network error]: ${networkError}`);
    return result;
  });

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem("user:token");
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const getClient = props => {
  const errorLink = getErrorLink(props);
  return new ApolloClient({
    link: from([errorLink, authLink.concat(httpLink)]),
    cache: new InMemoryCache(),
  });
};

export default getClient;
